import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch } from "react-redux";
import { AppDispatch } from "./../../../Redux/Store";
import {
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useCheckTokenExpiryMutation,
} from "./../../../Services/AuthService";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  CircularProgress,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FaArrowLeft } from "react-icons/fa6";
import TurnstileWidget from "../../../Helpers/TurnstileWidget";
import { FaCheck, FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import { logout } from "../../../Redux/Slices/AuthSlice";
import { useLogoutUserMutation } from "../../../Services/RolesService";

interface ForgotPasswordInputs {
  email: string;
}

interface ResetPasswordInputs {
  password: string;
  confirmPassword: string;
}

export const ForgotPassword: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [forgotPassword, { isLoading, isSuccess, error }] =
    useForgotPasswordMutation();
  const { register, handleSubmit, watch } = useForm<ForgotPasswordInputs>();
  const [captchValid, setCaptchValid] = useState(false);
  // The token, not just a boolean saying one existed. Discarded until now, so
  // the server could not tell a solved captcha from none at all.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const onSubmit: SubmitHandler<ForgotPasswordInputs> = async (data) => {
    await forgotPassword({ ...data, captchaToken });
  };

  const handleSuccess = (token: string) => {
    setCaptchaToken(token);
    setCaptchValid(true);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: alpha(theme.palette.background.default, 0.95),
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            padding: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h5" gutterBottom align="center">
            Forgot Password
          </Typography>

          {isSuccess && (
            <Typography color="success.main" align="center" mb={2}>
              Password reset link has been sent to your email.
            </Typography>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              type="email"
              {...register("email", { required: true })}
            />

            {error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "rgb(220 252 231 / var(--tw-bg-opacity, 1))",
                  border:
                    "1px solid rgb(21 128 61 / var(--tw-text-opacity, 1))",
                  color: "rgb(21 128 61 / var(--tw-text-opacity, 1))",
                  // borderRadius: 1,
                  fontSize: {
                    xs: "0.875rem",
                    sm: "1rem",
                  },
                }}
              >
                <Typography>
                  {(error as any).data?.message || "Something went wrong"}
                </Typography>
              </Box>
            )}

            <TurnstileWidget onSuccess={handleSuccess} />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{ mt: 2 }}
              disabled={isLoading || !captchValid}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Get Password Reset Link"
              )}
            </Button>
          </form>

          <Box textAlign="center" mt={2}>
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Typography variant="body2" color="primary">
                Back to Login
              </Typography>
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export const ResetPassword: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingToken, setIsLoadingToken] = useState(true);
  const [message, setMessage] = useState({ msg: "", type: "" });
  const token: string | null = searchParams.get("token") || null;
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logoutUser] = useLogoutUserMutation();
  const [resetPassword, { isLoading, isSuccess, error }] =
    useResetPasswordMutation();
  const [
    checkTokenExpiry,
    {
      isLoading: checkTokenExpiryLoading,
      isSuccess: checkTokenExpirySuccess,
      error: checkTokenExpiryError,
    },
  ] = useCheckTokenExpiryMutation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordInputs>();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const password = watch("password");
  const isTouched = typeof password === "string" && password.length > 0;

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[@$!%*?&]/.test(password);
  const isMinLength = typeof password === "string" && password.length >= 8;

  const onSubmit: SubmitHandler<ResetPasswordInputs> = async (data) => {
    if (!token) return;
    await resetPassword({ token, password: data.password });
  };

  useEffect(() => {
    if (!isSuccess) return;
    const t = setTimeout(async () => {
      // Setting a password means someone is taking this browser over -- typically
      // a new employee opening their invite on a machine where the admin who
      // created them is still signed in. Without clearing, isAuthenticated stays
      // true, so the router serves AdminRoutes, where "login" is <AuthRedirect />
      // and sends them into the PREVIOUS user's first permitted screen, still
      // authenticated as them. That is the reported bug.
      // Revoke server-side FIRST. dispatch(logout()) clears localStorage, and
      // AdminLayouts' logout-event handler reads the refresh token from there --
      // so clearing first made it call /employee/logout?refreshToken= with an
      // empty value, which 400s and leaves the previous user's refresh token
      // valid on the server. In a handover that is exactly the token to revoke.
      const refreshToken =
        localStorage.getItem(
          `${process.env.REACT_APP_STORAGE_KEY}/refreshToken`
        ) ?? "";
      if (refreshToken) {
        // Best effort: a failed revoke must not strand the user on this page.
        try {
          await logoutUser(refreshToken).unwrap();
        } catch {
          /* ignore -- local session is cleared regardless */
        }
      }
      dispatch(logout());
      // Sign other open tabs out too -- AdminLayouts listens for this key.
      localStorage.setItem("logout-event", Date.now().toString());
      // Hard reload rather than navigate(): App calls createAppRouter on every
      // render with no memo, so flipping isAuthenticated builds a new router and
      // remounts this tree mid-transition. A full load lands on a clean login
      // deterministically.
      window.location.hash = "#/login";
      window.location.reload();
    }, 3000);
    return () => clearTimeout(t);
  }, [isSuccess, dispatch]);

  useEffect(() => {
    if (!token) return;
    checkTokenExpiryHandler(token);
  }, [token]);

  const checkTokenExpiryHandler = async (token: string) => {
    const result: any = await checkTokenExpiry({ token });
    if (result && result?.error && result?.error?.data) {
      setIsLoadingToken(false);
      if (result?.error?.data) {
        setMessage({
          msg: "The password reset link you attempted to use is no longer valid as it has expired. To reset your password, please initiate a new password reset request.",
          type: "error",
        });
      }
    } else {
      setIsLoadingToken(false);
    }
  };

  const CriteriaItem = ({
    label,
    isValid,
    isTouched,
  }: {
    label: string;
    isValid: boolean;
    isTouched: boolean;
  }) => {
    let colorClass = "gray";
    if (isTouched) {
      colorClass = isValid ? "green" : "red";
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {isValid ? (
          // <FaCheck className={`mr-2 ${colorClass}`} />
          <FaCheck style={{ marginRight: 3, color: colorClass }} />
        ) : (
          // <FaTimes className={`mr-2 ${colorClass}`} />
          <FaTimes style={{ marginRight: 2, color: colorClass }} />
        )}
        <span style={{ color: colorClass }}>{label}</span>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: alpha(theme.palette.background.default, 0.95),
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            padding: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h5" gutterBottom align="center">
            Reset Password
          </Typography>

          {isSuccess ? (
            <Typography color="success.main" align="center">
              Password reset successfully! Redirecting to login...
            </Typography>
          ) : (
            <section>
              {isLoadingToken ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100px",
                  }}
                >
                  <CircularProgress />
                  <Typography>Loading...</Typography>
                </Box>
              ) : null}
              {!isLoadingToken && message && message?.msg && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color={message.type === "error" ? "error" : "success"}
                  >
                    {message.msg}
                  </Typography>
                  <Box textAlign="center" mt={2}>
                    <Link
                      to="/forgot-password"
                      replace
                      style={{
                        textDecoration: "none",
                        textDecorationLine: "underline",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <FaArrowLeft
                        size={16}
                        color={theme.palette.primary.main}
                      />
                      <Typography variant="body2" color="primary">
                        Back to Forgot Password
                      </Typography>
                    </Link>
                  </Box>
                </Box>
              )}
              {!isLoadingToken && message && message?.msg === "" ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    {...register("password", {
                      required: "Password is required",
                      // minLength: {
                      //   value: 8,
                      //   // message: "Minimum 8 characters required",
                      // },
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ marginRight: 1 }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Criteria - only show after user starts typing */}
                  {isTouched && (
                  <Box>
                    <CriteriaItem
                      label="At least 8 characters"
                      isValid={isMinLength}
                      isTouched={isTouched}
                    />
                    <CriteriaItem
                      label="Contains uppercase (A-Z)"
                      isValid={hasUpper}
                      isTouched={isTouched}
                    />
                    <CriteriaItem
                      label="Contains lowercase (a-z)"
                      isValid={hasLower}
                      isTouched={isTouched}
                    />
                    <CriteriaItem
                      label="Contains number (0-9)"
                      isValid={hasNumber}
                      isTouched={isTouched}
                    />
                    <CriteriaItem
                      label="Contains special character (@$!%*?&)"
                      isValid={hasSpecial}
                      isTouched={isTouched}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        fontSize: "13px",
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? "gray.400"
                            : "gray.500",
                      }}
                    >
                      Password must include all the criteria above.
                    </Typography>
                  </Box>
                  )}

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    {...register("confirmPassword", {
                      validate: (value) =>
                        value === watch("password") || "Passwords do not match",
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            sx={{ marginRight: 1 }}
                          >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <Typography color="error" variant="body2" gutterBottom>
                      {(error as any).data?.message || "Something went wrong"}
                    </Typography>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{ mt: 2 }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </form>
              ) : null}
            </section>
          )}
        </Box>
      </Container>
    </Box>
  );
};
